import { type TApiResponse } from "@/types/api";
import { handleAsync } from "@/utils";
import { ApiService, type TApiServiceError, type TApiServiceOptions } from "../apiService";
import { withAuthHooks } from "../auth/auth.ky.hooks";
import {
  type TTodoCategoryGroup,
  type TTodoPendingTasksResponse,
  type TTodoTask,
  type TTodoTaskSubmitResponse,
  type TTodoTaskSubmitResponsePayload,
} from "./todo.types";

type TTodoTaskRaw = {
  id: string;
  title: string;
  content: string;
  done: boolean;
  evaluated: boolean;
  priority?: number;
  section_id: string;
  section_category_id: string;
  company_id: string;
  task_type: {
    id: string;
    name: string;
  } | null;
};

type TTodoCategoryGroupRaw = {
  section_category_id: string;
  tasks: TTodoTaskRaw[];
};

type TTodoPendingTasksRawResponse = {
  code: number;
  message: string;
  data: TTodoCategoryGroupRaw[];
};

const apiService = new ApiService({
  hooks: {
    ...withAuthHooks,
  },
});

class TodoService {
  private rootEndpoint = "todo/todo-tasks" as const;

  getPendingTasks(companyId: string, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TTodoPendingTasksResponse>(async () => {
      const response = await apiService.post<TTodoPendingTasksRawResponse>(
        `${this.rootEndpoint}/pending`,
        { company_id: companyId },
        options
      );

      const groups: TTodoCategoryGroup[] = Array.isArray(response.data)
        ? response.data.map((group) => {
            const normalizedTasks: TTodoTask[] = Array.isArray(group.tasks)
              ? group.tasks.map((task) => ({
                  id: task.id,
                  title: task.title,
                  content: task.content,
                  done: task.done,
                  evaluated: task.evaluated,
                  priority: typeof task.priority === "number" ? task.priority : 6,
                  sectionId: task.section_id,
                  sectionCategoryId: task.section_category_id,
                  companyId: task.company_id,
                  taskType: task.task_type
                    ? {
                        id: task.task_type.id,
                        name: task.task_type.name,
                      }
                    : null,
                }))
              : [];

            return {
              sectionCategoryId: group.section_category_id,
              tasks: normalizedTasks,
            };
          })
        : [];

      return {
        code: response.code,
        message: response.message,
        data: groups,
      };
    });
  }

  updateTaskStatus(taskId: string, payload: { done: boolean; evaluated?: boolean }, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<{ done: boolean }>>(() =>
      apiService.put(
        `${this.rootEndpoint}/${taskId}/status`,
        {
          done: payload.done,
          evaluated: payload.evaluated ?? false,
        },
        options
      )
    );
  }

  submitTaskResponse(taskId: string, payload: TTodoTaskSubmitResponsePayload, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TTodoTaskSubmitResponse>>(() =>
      apiService.post(
        `${this.rootEndpoint}/${taskId}/submit-response`,
        {
          response_text: payload.responseText ?? null,
          file_ids: payload.fileIds ?? [],
        },
        options
      )
    );
  }
}

export const todoService = new TodoService();
